import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { colors } from 'theme/colors';
import { Box, IconButton, SxProps, Typography } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { useTranslations } from 'hooks/useTranslations';
import { EButtonSizesPX, EButtonStyleVariant, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { StyledTextField } from './styles';
import { getNestedErrorMessage } from './helpers';

export enum EFormInputTypes {
  text = 'text',
  textarea = 'textarea',
  password = 'password',
  number = 'number',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  type?: EFormInputTypes;
  sx?: SxProps;
  className?: string;
  InputProps?: object;
  disabled?: boolean;
  required?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  setValue?: any;
  fieldName?: string;
  helperText?: string;
  charsLimit?: number;
  defaultValue?: any;
  withValidation?: boolean;
  withLabel?: boolean;
  showCounter?: boolean;
  autoFocus?: boolean;
  onClickSearch?: (arg: string) => void;
  endAdornmentText?: string;
}

export const FormInput = ({
  name,
  type = EFormInputTypes.text,
  label,
  placeholder,
  multiline,
  minRows,
  control,
  errors,
  sx,
  setValue,
  className,
  InputProps,
  disabled,
  required,
  arrayName,
  arrayIndex,
  fieldName,
  helperText,
  charsLimit,
  defaultValue,
  withValidation = true,
  withLabel = true,
  showCounter = false,
  autoFocus = false,
  onClickSearch,
  endAdornmentText,
}: IFormInput) => {
  const { t } = useTranslations();
  const [error, setError] = useState<string>('');
  const [counterText, setCounterText] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  if (charsLimit && showCounter && !counterText) {
    setCounterText(`0/${charsLimit}`);
  }

  const renderEndAdornment = (field: ControllerRenderProps) => {
    if (type === EFormInputTypes.password) {
      return (
        <IconButton
          sx={{
            maxHeight: '32px',
            maxWidth: '32px',
            alignSelf: 'center',
            marginRight: '8px',
            marginLeft: '8px',
          }}
          onClick={() => setIsPasswordVisible((data) => !data)}
        >
          <Icon type={isPasswordVisible ? EIconTypes.view : EIconTypes.hidden} />
        </IconButton>
      );
    }
    if (onClickSearch) {
      return (
        <Button
          variant={EButtonVariants.contained}
          icon={EIconTypes.search}
          sizePX={EButtonSizesPX.px40}
          onClick={() => {
            onClickSearch(field.value);
          }}
          styleVariant={EButtonStyleVariant.primary}
          sx={{ borderRadius: '0 4px 4px 0 !important' }}
        />
      );
    }

    const showClearButton =
      field.value !== '' &&
      !disabled &&
      setValue &&
      field.value !== undefined &&
      field.value !== null;

    if (showClearButton || endAdornmentText) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="row"
          sx={{
            backgroundColor: disabled ? colors.gray100 : 'inherit',
          }}
        >
          {showClearButton && (
            <IconButton
              sx={{
                maxHeight: '32px',
                maxWidth: '32px',
              }}
              onClick={() => {
                setValue(name, '');
                setError('');
                setCounterText('');
              }}
            >
              <Icon type={EIconTypes.clear} />
            </IconButton>
          )}
          {endAdornmentText && !disabled && (
            <Typography variant="body2" paddingLeft={1} paddingRight={2}>
              {endAdornmentText}
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <StyledFormElementWrapper>
      {withLabel && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <StyledTextField
            {...field}
            defaultValue={defaultValue}
            className={className}
            disabled={disabled}
            // isEmpty={!!isStringEmpty(field.value)}
            InputProps={{
              inputProps: {
                ...(showCounter && charsLimit ? { maxLength: charsLimit } : {}),
              },
              ...InputProps,
              endAdornment: renderEndAdornment(field),
            }}
            type={isPasswordVisible ? 'text' : type}
            id={name}
            variant="outlined"
            placeholder={placeholder}
            multiline={multiline}
            minRows={minRows}
            error={!!error}
            sx={sx}
            autoFocus={autoFocus}
            helperText={showCounter ? counterText : helperText}
            onChange={(e: BaseSyntheticEvent) => {
              setError('');
              field.onChange(e.target.value || '');
              if (charsLimit && showCounter) {
                setCounterText(`${(e.target.value || '').length}/${charsLimit}`);
              }

              if (charsLimit && !showCounter && charsLimit < e.target.value.length) {
                setError(
                  t('general.field.validation.maxCharacters').replace(
                    '{0}',
                    charsLimit.toString(),
                  ),
                );
              }
            }}
          />
        )}
      />
      {withValidation && <FormErrorLabel label={error} />}
    </StyledFormElementWrapper>
  );
};
